import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setFormDisplayTime } from 'src/slices/app';
import { useLocation } from 'react-router-dom';
// components
import { FormElement } from '@progress/kendo-react-form';
import BaseForm from 'shared/forms';
import FormActionBar from '../FormActionBar';
import UnsavedChanges from 'shared/modals/UnsavedChanges';

// eslint-disable-next-line no-unused-vars
const { BaseFormRenderProps, AdditionalFormActionBarProps, BaseFormProps } = require('./typedefs');

/**
 * @typedef {Object} AdditionalPageFormProps
 * @property {boolean} [allowCloseOnSubmit={true}] Indicates if Close&Submit is allowed. True by default.
 * @property {(props: BaseFormRenderProps) => any} renderFields The page form content that will be rendered.
 * @property {React.ReactElement} [customActionButtonsRender] Custom actions for the actions bar.
 * @property {string} [pageName] Represents the page form's item name.
 * @property {boolean} [hideActionBar={false}] Indicates if the action bar is hidden. False by default.
 * @property {boolean} [hideChangesSavedMessage={false}] Indicates if Unsaved changes prompt is not shown. False by default.
 * @property {boolean} [updateOnEvery={true}] Indicates if the form is re rendered on initial values changes. If true, form key is set to initialValues JSON string. True by default.
 * @property {boolean} [saveDisabled={false}] Indicates if the form's save button is disabled. False by default.
 * @typedef {AdditionalFormActionBarProps & Omit<BaseFormProps, 'render'> & AdditionalPageFormProps} PageFormProps
 *
 * @param {PageFormProps}  props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const PageForm = ({
  // form methods
  onSubmit,
  onAsyncDelete,
  onDelete,
  onClose,
  onEnableChange,
  allowCloseOnSubmit = true,
  // field rendering
  renderFields,
  customActionButtonsRender,
  hideSaveAndClose = false,
  hideSave = false,
  hideCloseButton = false,
  // delete props
  showDeleteButton,
  // enable state props
  isEnabled,
  showEnableStateButtons,
  // other props
  pageName,
  itemName,
  initialValues,
  hideActionBar = false,
  alwaysEnableSaveButton,
  hideChangesSavedMessage,
  disableUnsavedChangesWarning = false,
  customActions,
  readOnly = false,
  updateOnEvery = true,
  enableHotkeys = true,
  logDisplayTime = false,
  saveDisabled = false,
  ...props
}, ref) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  // Log display time
  useEffect(() => {
    if (logDisplayTime) {
      dispatch(setFormDisplayTime({ pathname, time: window.performance.now() }));
      return () => {
        dispatch(setFormDisplayTime(null));
      };
    }
    return () => {};
  }, []);

  const handleSubmit = async (values, onSuccess, onError, onFinish, event) => {
    const { closeOnSubmit } = values;
    if (!onSubmit) {
      return null;
    }

    try {
      const result = await onSubmit(values, event);
      if (onSuccess) {
        onSuccess();
      }
      if (closeOnSubmit && allowCloseOnSubmit) {
        onClose(true);
      }

      return result;
    } catch (error) {
      if (onError) {
        onError(error);
      }
    } finally {
      if (onFinish) {
        onFinish();
      }
    }
  };

  return (
    <BaseForm
      ref={ref}
      ignoreModified
      initialValues={initialValues}
      key={updateOnEvery ? JSON.stringify(initialValues) : false} // We need to set the key to reset the form
      onSubmit={handleSubmit}
      enableHotkeys={enableHotkeys}
      {...props}
      render={(renderParams) => {
        const {
          modified, touched, isSubmitting, valueGetter,
        } = renderParams;

        return (
          <FormElement>
            {!disableUnsavedChangesWarning && (
              <UnsavedChanges dirty={modified && touched && !isSubmitting && !valueGetter('closeOnSubmit')} />
            )}
            {renderFields(renderParams)}
            {!hideActionBar && (
              <FormActionBar
                itemName={itemName || pageName || 'this'}
                onClose={() => onClose(false)}
                onDelete={onDelete}
                onAsyncDelete={onAsyncDelete}
                showDeleteButton={showDeleteButton}
                isEnabled={isEnabled}
                showEnableStateButtons={showEnableStateButtons}
                onEnableChange={onEnableChange}
                customRender={customActionButtonsRender}
                hideSaveAndClose={hideSaveAndClose}
                hideCloseButton={hideCloseButton}
                hideSave={hideSave}
                saveDisabled={saveDisabled}
                alwaysEnableSaveButton={alwaysEnableSaveButton}
                hideChangesSavedMessage={hideChangesSavedMessage}
                readOnly={readOnly}
                enableHotkeys={enableHotkeys}
                /// submitted={submitting}
                customActions={customActions}
                saveTitle={props.saveTitle || 'Save'}
                {...renderParams}
              />
            )}
          </FormElement>
        );
      }}
    />
  );
};

/**
 * A page form component with various options and handlers. Used for details page form.
 *
 * @param {PageFormProps} props Component props. Extends BaseForm props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Page form component.
 */
const ForwardPageForm = React.forwardRef(PageForm);

export default ForwardPageForm;
